import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom'
import { Routes } from 'routes'
import { Suspense, lazy } from 'react'
import { AppLayout, AuthLayout, Spinner } from '../components'
import { Landing } from '../pages/Landing'
import { MarkdownPage } from '../pages/MarkdownPage'

const lazyWithSuspense = (
  importFn: () => Promise<{ default: React.ComponentType }>,
) => {
  const LazyComponent = lazy(importFn)
  return () => (
    <Suspense fallback={<Spinner />}>
      <LazyComponent />
    </Suspense>
  )
}
import { appConfig } from '@/ui/core/config'
import styled from 'styled-components'
import { Contact } from '../pages/Contact'
import { Pricing } from '../pages/Pricing'
import { Authentication } from '../pages/Authentication'

// Lazy load all page components for better code splitting
const WelcomeEvent = lazyWithSuspense(() =>
  import('../pages/WelcomeEvent').then((module) => ({
    default: module.WelcomeEvent,
  })),
)

const EntryFeed = lazyWithSuspense(() =>
  import('../pages/EntryFeed').then((module) => ({
    default: module.EntryFeed,
  })),
)

const CreateEntry = lazyWithSuspense(() =>
  import('../pages/CreateEntry').then((module) => ({
    default: module.CreateEntry,
  })),
)

const ClosingTime = lazyWithSuspense(() =>
  import('../pages/ClosingTime').then((module) => ({
    default: module.ClosingTime,
  })),
)

const PageNotFound = lazyWithSuspense(() =>
  import('../pages/PageNotFound').then((module) => ({
    default: module.PageNotFound,
  })),
)

// Admin/Auth-required pages (these can be in a separate chunk)
const CreateEvent = lazy(() =>
  import('../pages/CreateEvent').then((module) => ({
    default: module.CreateEvent,
  })),
)
const Dashboard = lazy(() =>
  import('../pages/Dashboard').then((module) => ({
    default: module.Dashboard,
  })),
)
const Settings = lazy(() =>
  import('../pages/Settings').then((module) => ({ default: module.Settings })),
)
const EventDetail = lazy(() =>
  import('../pages/EventDetail').then((module) => ({
    default: module.EventDetail,
  })),
)
const ShareEvent = lazy(() =>
  import('../pages/ShareEvent').then((module) => ({
    default: module.ShareEvent,
  })),
)
const EditBasic = lazy(() =>
  import('../pages/EditBasic').then((module) => ({
    default: module.EditBasic,
  })),
)
const EditClosing = lazy(() =>
  import('../pages/EditClosing').then((module) => ({
    default: module.EditClosing,
  })),
)
const EditWelcome = lazy(() =>
  import('../pages/EditWelcome').then((module) => ({
    default: module.EditWelcome,
  })),
)
const OwnerFeed = lazy(() =>
  import('../pages/OwnerFeed').then((module) => ({
    default: module.OwnerFeed,
  })),
)
const RedirectPage = lazy(() =>
  import('../pages/RedirectPage').then((module) => ({
    default: module.RedirectPage,
  })),
)

// Heavy feature pages that should be loaded on demand
const InvitationEditor = lazy(() =>
  import('../pages/InvitationEditor').then((module) => ({
    default: module.InvitationEditor,
  })),
)
const CreateBook = lazy(() =>
  import('../pages/CreateBook').then((module) => ({
    default: module.CreateBook,
  })),
)
const PreviewPDF = lazy(() =>
  import('../pages/PreviewPDF').then((module) => ({
    default: module.PreviewPDF,
  })),
)

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 18px;
  color: #666;
`
// Loading component for Suspense
const PageLoading = () => (
  <LoadingContainer>
    <Spinner />
  </LoadingContainer>
)

// FUTURE INVESTIGATION: See if using `loader` is better than useQuery to load initial data
export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/" element={<AppLayout showFooter={true} />}>
        {/* Don't lazy load Landing page as we want it to be loaded immediately */}
        <Route path={Routes.landing} element={<Landing />} />
        <Route path={Routes.contact} element={<Contact />} />
        <Route path={Routes.authentication} element={<Authentication />} />
        {appConfig.enablePricing && (
          <Route path={Routes.pricing} element={<Pricing />} />
        )}
        <Route path={Routes.terms} element={<MarkdownPage />} />
        <Route path={Routes.privacy} element={<MarkdownPage />} />
        <Route
          path={`${Routes.welcomeEvent}:eventId`}
          element={
            <Suspense fallback={<PageLoading />}>
              <WelcomeEvent />
            </Suspense>
          }
        />
        <Route
          path={`${Routes.entryFeed}:eventId`}
          element={
            <Suspense fallback={<PageLoading />}>
              <EntryFeed />
            </Suspense>
          }
        />
        <Route
          path={`${Routes.entryForm}:eventId`}
          element={
            <Suspense fallback={<PageLoading />}>
              <CreateEntry />
            </Suspense>
          }
        />
        <Route
          path={`${Routes.closingTime}:eventId`}
          element={
            <Suspense fallback={<PageLoading />}>
              <ClosingTime />
            </Suspense>
          }
        />
        <Route
          path={'*'}
          element={
            <Suspense fallback={<PageLoading />}>
              <PageNotFound />
            </Suspense>
          }
        />
      </Route>
      <Route path="/" element={<AppLayout />}>
        <Route element={<AuthLayout />}>
          <Route
            path={Routes.createEvent}
            element={
              <Suspense fallback={<PageLoading />}>
                <CreateEvent />
              </Suspense>
            }
          />
          <Route
            path={Routes.dashboard}
            element={
              <Suspense fallback={<PageLoading />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path={Routes.settings}
            element={
              <Suspense fallback={<PageLoading />}>
                <Settings />
              </Suspense>
            }
          />
          <Route
            path={`${Routes.eventDetail}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <EventDetail />
              </Suspense>
            }
          />
          <Route
            path={Routes.shareEvent}
            element={
              <Suspense fallback={<PageLoading />}>
                <ShareEvent />
              </Suspense>
            }
          />

          <Route
            path={`${Routes.editBasic}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <EditBasic />
              </Suspense>
            }
          />
          <Route
            path={`${Routes.editClosing}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <EditClosing />
              </Suspense>
            }
          />
          <Route
            path={`${Routes.editWelcome}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <EditWelcome />
              </Suspense>
            }
          />
          <Route
            path={`${Routes.ownerFeed}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <OwnerFeed />
              </Suspense>
            }
          />

          <Route
            path={`${Routes.invitationEditor}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <InvitationEditor />
              </Suspense>
            }
          />

          <Route
            path={`${Routes.createBook}:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <CreateBook />
              </Suspense>
            }
          />
          <Route
            path={Routes.redirectPage}
            element={
              <Suspense fallback={<PageLoading />}>
                <RedirectPage />
              </Suspense>
            }
          />
          {/* Temp for testing */}
          <Route
            path={`${'preview'}/:eventId`}
            element={
              <Suspense fallback={<PageLoading />}>
                <PreviewPDF />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </>,
  ),
)
